/* eslint-disable react/no-danger */
import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import DOMPurify from 'isomorphic-dompurify';
import { getAltTagFromFileName } from 'utils';
import { graphql } from 'gatsby';
import { LocalContext } from 'context';
import {
  SEO,
  Testimonials,
  Button,
  MultiColumnCarousel,
  StaircaseCard,
  SingleColumnCarousel,
  MobileStairFilters,
  Dialog,
  DismissDialog
} from 'components';
import { useWindowSize } from 'hooks';
import { Grid } from 'components/ProductPage/Shared';
import { Hero } from 'components/ProductCollectionPage/Shared';
import getFilters from '../local-data/stair-filters';

const getFilterButtons = ({
  filterNames,
  activeFilter,
  setActiveFilter,
  showStairFiltersDialog,
  handleStairFiltersDialog
}) =>
  filterNames.map((filterName) => (
    <Button
      key={filterName}
      variant={filterName === activeFilter ? 'contained' : 'outlined'}
      onClick={() => {
        if (showStairFiltersDialog) {
          handleStairFiltersDialog({
            animation: 'close',
            callback: () => setActiveFilter(filterName)
          });
        } else {
          setActiveFilter(filterName);
        }
      }}>
      {filterName}
    </Button>
  ));

const getSubFilterButtons = ({
  subFilterNames,
  activeSubFilter,
  setActiveSubFilter,
  showStairSubFiltersDialog,
  handleStairSubFiltersDialog
}) =>
  subFilterNames.map((subFilterName) => (
    <Button
      key={subFilterName}
      variant={subFilterName === activeSubFilter ? 'contained' : 'outlined'}
      onClick={() => {
        if (showStairSubFiltersDialog) {
          handleStairSubFiltersDialog({
            animation: 'close',
            callback: () => setActiveSubFilter(subFilterName)
          });
        } else {
          setActiveSubFilter(subFilterName);
        }
      }}>
      {subFilterName}
    </Button>
  ));

const getStyle = ({ title }) => {
  switch (title) {
    case 'Softwood Staircases':
      return {
        fontSize: '2.625rem'
      };
    case 'Primed Staircases':
      return {
        fontSize: '2.325rem'
      };
    case 'Glass Staircases':
      return {
        fontSize: '2.25rem',
        marginTop: '1.75rem'
      };
    default:
      return {
        fontSize: '2.5rem'
      };
  }
};

function StairsTemplate({ data }) {
  const { heroData, otherStairs } = data;
  const {
    title,
    heroImage,
    heroDescription,
    gradientPercentage,
    heroImageOffset,
    filter,
    carouselImages
  } = heroData.frontmatter;
  const { handleDialog } = useContext(LocalContext);
  const filters = getFilters(title);
  const [activeFilter, setActiveFilter] = useState(Object.entries(filters)[0][0]);
  const [activeSubFilter, setActiveSubFilter] = useState(
    Object.entries(filters[activeFilter])[0][0]
  );

  const filterNames = Object.entries(filters).map(([filterName]) => filterName);
  const subFilterNames = Object.entries(filters[activeFilter]).map((obj) => obj[0]);

  const [showStairFiltersDialog, setShowStairFiltersDialog] = useState(false);
  const [showStairSubFiltersDialog, setShowStairSubFiltersDialog] = useState(false);

  const { windowWidth } = useWindowSize();

  const stairFiltersDialogRef = useRef();
  const stairSubFiltersDialogRef = useRef();

  useEffect(() => {
    const _activeSubFilter = Object.entries(filters[activeFilter])[0][0];
    setActiveSubFilter(_activeSubFilter);
  }, [activeFilter]);

  const _otherStairs = otherStairs.nodes.map((node) => {
    const { id, frontmatter } = node;
    const { title: _title, slug, shortDescription, cardImage } = frontmatter;
    return (
      <StaircaseCard
        key={id}
        id={id}
        slug={`/stairs/${slug}`}
        image={cardImage.childImageSharp.gatsbyImageData}
        title={_title}
        description={shortDescription}
        alt={getAltTagFromFileName(cardImage.name)}
      />
    );
  });

  const images = carouselImages
    ? [
        ...carouselImages.map((obj) => ({
          asset: {
            _id: obj.image.id,
            gatsbyImageData: obj.image.childImageSharp.gatsbyImageData,
            originalFilename: obj.image.name
          }
        }))
      ]
    : [];

  const handleStairFiltersDialog = ({ animation, callback }) =>
    handleDialog({
      dialogRef: stairFiltersDialogRef,
      animation,
      stateHandler: () => setShowStairFiltersDialog(false),
      callback
    });

  const handleStairSubFiltersDialog = ({ animation, callback }) =>
    handleDialog({
      dialogRef: stairSubFiltersDialogRef,
      animation,
      stateHandler: () => setShowStairSubFiltersDialog(false),
      callback
    });

  return (
    <>
      <Hero
        heroImage={heroImage}
        gradientPercentage={gradientPercentage}
        heroImageOffset={heroImageOffset}
        filter={filter}
        heroImageAlt={getAltTagFromFileName(heroImage.name)}
        heroImageTitle="Stairs"
        showRequestAQuoteButton>
        <h1
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
          style={getStyle({ title })}
        />
        <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heroDescription) }} />
      </Hero>
      <Grid>
        <InnerGrid className="fade-in">
          <h2>Examples of {title}</h2>
          {windowWidth >= 1000 && (
            <>
              <FilterBy>Filter by:</FilterBy>
              <Filters>{getFilterButtons({ filterNames, activeFilter, setActiveFilter })}</Filters>
            </>
          )}
          <SingleColumnCarousel
            images={images}
            imageWidth={550}
            imageHeight={700}
            showIndicators
            markdownImages
          />
          <Text>
            <MobileStairFiltersContainer>
              <MobileStairFilters
                filterNames={filterNames}
                subFilterNames={subFilterNames}
                activeFilter={activeFilter}
                activeSubFilter={activeSubFilter}
                setShowStairFiltersDialog={setShowStairFiltersDialog}
                setShowStairSubFiltersDialog={setShowStairSubFiltersDialog}
              />
              <Dialog
                ref={stairFiltersDialogRef}
                isVisible={showStairFiltersDialog}
                onDismiss={() => handleStairFiltersDialog({ animation: 'dismiss' })}
                contentStyle={{ maxWidth: '260px' }}>
                <DialogContent>
                  <DismissDialog
                    onClick={() => handleStairFiltersDialog({ animation: 'dismiss' })}
                  />
                  {getFilterButtons({
                    filterNames,
                    activeFilter,
                    setActiveFilter,
                    showStairFiltersDialog,
                    handleStairFiltersDialog
                  })}
                </DialogContent>
              </Dialog>
              <Dialog
                ref={stairSubFiltersDialogRef}
                isVisible={showStairSubFiltersDialog}
                onDismiss={() => handleStairSubFiltersDialog({ animation: 'dismiss' })}
                contentStyle={{ maxWidth: '260px' }}>
                <DialogContent>
                  <DismissDialog
                    onClick={() => handleStairSubFiltersDialog({ animation: 'dismiss' })}
                  />
                  {getSubFilterButtons({
                    subFilterNames,
                    activeSubFilter,
                    setActiveSubFilter,
                    showStairSubFiltersDialog,
                    handleStairSubFiltersDialog
                  })}
                </DialogContent>
              </Dialog>
            </MobileStairFiltersContainer>
            <DesktopStairFiltersContainer>
              <h2>{activeFilter}</h2>
              <p style={{ color: 'var(--connolly-red)' }}>Show:</p>
              <SubFilters>
                {getSubFilterButtons({ subFilterNames, activeSubFilter, setActiveSubFilter })}
              </SubFilters>
            </DesktopStairFiltersContainer>
            {filterNames.map((filterName) =>
              Object.keys(filters[filterName]).map((subFilterName) => (
                <p
                  key={`${filterName}-${subFilterName}`}
                  className={
                    filterName === activeFilter && subFilterName === activeSubFilter
                      ? ''
                      : 'visually-hidden'
                  }
                  aria-hidden={!(filterName === activeFilter && subFilterName === activeSubFilter)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(filters[filterName][subFilterName])
                  }}
                />
              ))
            )}
            <Button to="/request-a-quote/" shine shineDuration={3}>
              Request A Quote
            </Button>
          </Text>
        </InnerGrid>
      </Grid>
      <StyledMultiColumnCarousel
        title="View Other Stairs"
        cards={_otherStairs}
        backgroundColor={windowWidth < 590 ? '#fff' : 'var(--connolly-grey)'}
        responsive={{
          desktop: {
            breakpoint: { max: 10000, min: 1300 },
            items: 3
          },
          tablet: {
            breakpoint: { max: 1299, min: 914 },
            items: 2
          },
          mobile: {
            breakpoint: { max: 913, min: 0 },
            items: 1
          }
        }}
      />
      <StyledTestimonials backgroundColor={windowWidth < 590 ? '#fff' : 'var(--connolly-grey)'} />
    </>
  );
}

const MobileStairFiltersContainer = styled.div`
  @media (width >= 1100px) {
    display: none;
  }
`;

const DesktopStairFiltersContainer = styled.div`
  @media (width <= 1099px) {
    display: none;
  }
`;

const DialogContent = styled.div`
  align-items: center;
  background-color: #f1f1f1;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  padding: 50px;
  width: 260px;

  button {
    border-radius: 100px;
    font-size: 0.875rem;
    height: 40px;
    text-transform: capitalize;
    white-space: nowrap;
  }
`;

const InnerGrid = styled.div`
  background-color: #fff;
  border-radius: 0.375rem;
  grid-column: 1/-1;
  grid-template-columns: subgrid;
  justify-content: center;
  justify-self: center;
  margin-top: 0;
  max-width: 590px;
  padding: 2.5rem 30px 2.125rem;
  width: 100%;
  z-index: 1;

  @media (width >= 768px) {
    margin-top: -35px;
  }

  @media (width >= 1000px) {
    display: grid;
    grid-auto-flow: dense;
    grid-column: 2/12;
    max-width: initial;
    padding: 3.95rem 30px 2.125rem;
  }

  /* Carousel */
  > :nth-last-child(2) {
    width: 100%;

    @media (width >= 1000px) {
      grid-column: 6/11;
      width: calc(100% + 10px);
    }
  }

  > h2 {
    font-size: 2rem;
    grid-column: 1/-1;
    margin-bottom: 27px;
    text-align: center;
  }
`;

const FilterBy = styled.p`
  color: var(--connolly-red);
  grid-column: 1/-1;
  margin-bottom: 18px;
  text-align: center;
`;

const Filters = styled.div`
  display: flex;
  gap: 20px;
  grid-column: 1/-1;
  grid-template-columns: auto auto auto;
  justify-self: center;
  margin-bottom: 50px;

  button {
    border-radius: 100px;
    font-size: 0.875rem;
    grid-row: 2;
    height: 40px;
    text-transform: capitalize;
    white-space: nowrap;
  }

  button:nth-of-type(1) {
    width: 136px;
  }

  button:nth-of-type(2) {
    width: 120px;
  }

  button:nth-of-type(3) {
    width: 148px;
  }
`;

const SubFilters = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 2.3em;
  margin-top: 0.825em;

  @media (width >= 1200px) {
    gap: 20px;
  }

  button {
    border-radius: 100px;
    font-size: 0.875rem;
    grid-row: 2;
    height: 40px;
    text-transform: capitalize;
    white-space: nowrap;
  }
`;

const Text = styled.div`
  grid-column: 1/6;
  margin-top: 33px;
  position: relative;

  h2 {
    font-size: 2rem;
    margin-bottom: 0.825em;
    text-align: left !important;
  }

  p {
    max-width: 480px;
    width: 100%;
  }

  /* Request A Quote button */
  > :last-child {
    display: inline-block;
    margin-top: 40px;
  }

  .visually-hidden {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute !important;
    white-space: nowrap;
    width: 1px;
  }
`;

const StyledMultiColumnCarousel = styled(MultiColumnCarousel)`
  padding-bottom: 112px;
  width: 100%;

  > h3 {
    padding: 3.05em 0 0.85em;

    @media (width >= 768px) {
      padding: 3.35em 0 1.1em;
    }
  }

  > div {
    /* Width of Arrow Buttons */
    max-width: 360px;

    /* Width of Slides container */
    > div {
      max-width: 360px;
    }
  }

  @media (width >= 380px) {
    > div {
      max-width: 440px;

      > div {
        max-width: 380px;
      }
    }
  }

  @media (width >= 914px) {
    padding-bottom: 39px;

    > div {
      max-width: 820px;

      > div {
        max-width: 760px;
      }
    }
  }

  @media (width >= 1300px) {
    > div {
      max-width: 1200px;

      > div {
        max-width: 1140px;
      }
    }
  }

  .react-multi-carousel-dot button {
    background-color: #c4c4c4;
    border: none;
    width: 0.625rem;
    height: 0.625rem;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }

  .react-multi-carousel-item a {
    height: 30.375rem;
    width: 22.5rem;

    .gatsby-image-wrapper {
      height: 15rem;
    }
  }
`;

const StyledTestimonials = styled(Testimonials)`
  > h3 {
    padding: 1.9em 0 1.25em;
  }
`;

export const query = graphql`
  query ($stairType: String!) {
    heroData: markdownRemark(frontmatter: { title: { eq: $stairType } }) {
      id
      frontmatter {
        title
        heroDescription
        metaTitle
        metaDescription
        heroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
        gradientPercentage
        heroImageOffset
        filter
        carouselImages {
          image {
            id
            name
            childImageSharp {
              gatsbyImageData(width: 550, height: 700, quality: 90)
            }
          }
        }
      }
    }
    otherStairs: allMarkdownRemark(
      filter: {
        frontmatter: { title: { ne: $stairType } }
        fileAbsolutePath: { regex: "/local-data/stairs/collections/" }
      }
    ) {
      nodes {
        id
        frontmatter {
          title
          slug
          shortDescription
          cardImage {
            name
            childImageSharp {
              gatsbyImageData(width: 360, height: 240, formats: [AUTO, WEBP, AVIF])
            }
          }
        }
      }
    }
  }
`;

export function Head({ data, location }) {
  const { title, metaTitle, metaDescription } = data.heroData.frontmatter;
  return (
    <SEO title={metaTitle} description={metaDescription}>
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Stairs Ireland",
            "item": "https://www.connollystairs.ie/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Stairs",
            "item": "https://www.connollystairs.ie/stairs/"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "${title}",
            "item": "https://www.connollystairs.ie${location.pathname}"
          }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default StairsTemplate;
